import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import { ProjectPlayer } from '../../../../app/components/audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import { getSearchResults } from '../../../../app/features/sessions/sessionSearch/sliceSearch';
import ProjectDetailsModal from '../../../../app/components/modal/projectModal/ProjectDetails';

const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface ProjectsSearch {
    searchType: string;
    query: string | null;
}

export function ProjectsSearch({ searchType, query }: ProjectsSearch) {
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const dispatch = useDispatch<AppDispatch>();
    const [projectsSearchPage, setProjectsSearchPage] = useState(1);
    const MemorizedProjectPlayer = useMemo(() => React.memo(ProjectPlayer), []);
    const results = useSelector((state: RootState) => state.sessionSearch.results);
    const loadingMoreResults = useSelector((state: RootState) => state.sessionSearch.loadingMoreResults);
    const [isFetching, setIsFetching] = useState(false);
    const [showProjectDetails, setShowProjectDetails] = useState(false);
    const [projectSelected, setProjectSelected] = useState<any>();
    const [toggleModalDescriptionOpen, setToggleModalDescriptionOpen] = useState(false);

    useEffect(() => {
        async function fetchProjectsData() {
            if (authHeaders && searchType && query && !isFetching) {
                await dispatch(
                    getSearchResults({
                        headers: authHeaders,
                        routeParams: {
                            queryType: searchType,
                            query: query,
                            page: projectsSearchPage,
                        },
                    })
                );
                setIsFetching(false);
            }
        }

        fetchProjectsData();
    }, [searchType, projectsSearchPage]);

    useEffect(() => {
        function handleScroll() {
            if (
                window.innerHeight + document.documentElement.scrollTop !==
                    document.documentElement.offsetHeight ||
                isFetching
            )
                return;
            setProjectsSearchPage((prevPage) => prevPage + 1);
        }

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isFetching]);

    function handleFormatDate(date: any) {
        const originalDate = new Date(date);
        const formattedDate = format(originalDate, 'dd MMM yyyy HH:mm:ss');
        return formattedDate;
    }

    function toggleDescriptionModal(item: any) {
        setProjectSelected(item);
        setToggleModalDescriptionOpen((prev) => !prev);
        setShowProjectDetails((prev) => !prev);
    }

    return (
        <Box>
            {toggleModalDescriptionOpen && (
                <ProjectDetailsModal
                    projectInfoAndUrls={projectSelected}
                    close={() => setToggleModalDescriptionOpen((prev) => !prev)}
                />
            )}

            {results.length > 0 &&
                results.map((item: any, index: number) => (
                    <Box
                        key={index}
                        sx={{
                            bgcolor: BG_SECTION,
                            border: BORDER_SUBTLE,
                            borderRadius: '10px',
                            mb: 1.5,
                        }}
                    >
                        {/* Topo: título + privacidade + botão info */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                px: 2,
                                pt: 1.5,
                                pb: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    color: TEXT_PRIMARY,
                                    fontWeight: 700,
                                    fontSize: 15,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.project.title}
                            </Typography>

                            <Chip
                                label={item.privacity === 0 ? 'PUBLIC' : 'PRIVATE'}
                                size="small"
                                sx={{
                                    bgcolor:
                                        item.privacity === 0
                                            ? 'rgba(46,204,113,0.15)'
                                            : 'rgba(192,57,43,0.18)',
                                    color: item.privacity === 0 ? '#2ecc71' : BRAND_RED,
                                    fontWeight: 700,
                                    fontSize: 10,
                                    height: 20,
                                    flexShrink: 0,
                                }}
                            />

                            <IconButton
                                size="small"
                                onClick={() => toggleDescriptionModal(item)}
                                sx={{
                                    color: TEXT_SECONDARY,
                                    flexShrink: 0,
                                    '&:hover': {
                                        color: TEXT_PRIMARY,
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                    },
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Stats */}
                        <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 1 }}>
                            <Chip
                                label={`Useful: ${item.nUsefulContents}`}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.06)',
                                    color: TEXT_SECONDARY,
                                    fontSize: 11,
                                    height: 22,
                                }}
                            />
                            <Chip
                                label={`Staging: ${item.nUselessContents}`}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.06)',
                                    color: TEXT_SECONDARY,
                                    fontSize: 11,
                                    height: 22,
                                }}
                            />
                        </Box>

                        {/* Player */}
                        {item.masterFiles && item.masterFiles.length > 0 && (
                            <Box sx={{ px: 2, pb: 1.5 }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: TEXT_SECONDARY, display: 'block', mb: 0.5 }}
                                >
                                    {handleFormatDate(item.masterFiles[0].createdAt)}
                                </Typography>
                                <MemorizedProjectPlayer
                                    fileUrl={`${item.masterFiles[0].signedUrl}`}
                                />
                            </Box>
                        )}
                    </Box>
                ))}

            {/* Estado vazio */}
            {results.length === 0 && !loadingMoreResults && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mt: 8,
                        gap: 2,
                    }}
                >
                    <LibraryMusicIcon sx={{ fontSize: 56, color: TEXT_SECONDARY }} />
                    <Typography sx={{ color: TEXT_SECONDARY, fontSize: 16 }}>
                        Nothing to show
                    </Typography>
                </Box>
            )}

            {/* Loading */}
            {loadingMoreResults && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress sx={{ color: BRAND_RED }} />
                </Box>
            )}
        </Box>
    );
}
